/**
 * EnrollmentStatusPill — shared status pill for the study enrollment-status
 * domain enum (startup / enrolling / closed_to_enrollment / completed /
 * terminated). Hues are data, not chrome: these colors identify the status,
 * they are not a chrome accent. Canonical label/color meta lives here so
 * Site Organizer (studyorganizer/components/StatusBadge) and Operations
 * Insights (insights/components/StudyTile) render the exact same pill
 * instead of maintaining their own copies. `status` is intentionally typed
 * `string` (not the studyorganizer `EnrollmentStatus` union) so any caller
 * can pass it without a module dependency; an unrecognized value falls back
 * to a gray pill showing the raw value.
 */

export interface EnrollmentStatusMeta {
  label: string;
  color: string;
}

// Canonical value -> {label, color}. Site Organizer owns this domain
// (studyorganizer/types ENROLLMENT_STATUSES) — labels/colors here match it
// verbatim; do not drift the two apart.
export const ENROLLMENT_STATUS_META: Record<string, EnrollmentStatusMeta> = {
  startup: { label: 'Startup', color: 'yellow' },
  enrolling: { label: 'Enrolling', color: 'green' },
  closed_to_enrollment: { label: 'Closed to Enrollment', color: 'gray' },
  completed: { label: 'Completed', color: 'blue' },
  terminated: { label: 'Terminated', color: 'red' },
};

const COLOR_CLASSES: Record<string, string> = {
  yellow: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300',
  green: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300',
  gray: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
  blue: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300',
  red: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300',
};

const SIZE_CLASSES: Record<'sm' | 'md', string> = {
  sm: 'px-2 py-0.5 text-xs',
  md: 'px-2.5 py-0.5 text-xs',
};

interface EnrollmentStatusPillProps {
  /** Enrollment-status enum value. Unknown values render a gray pill with the raw value as label. */
  status: string;
  size?: 'sm' | 'md';
}

export function EnrollmentStatusPill({ status, size = 'md' }: EnrollmentStatusPillProps) {
  const meta = ENROLLMENT_STATUS_META[status];
  const label = meta?.label || status;
  const colorClasses = COLOR_CLASSES[meta?.color || 'gray'];

  return (
    <span className={`inline-flex items-center rounded-full font-medium whitespace-nowrap ${colorClasses} ${SIZE_CLASSES[size]}`}>
      {label}
    </span>
  );
}

export default EnrollmentStatusPill;
